/*
 * IRONiNK pricing guard
 * ---------------------
 * Some personalization boxes (for example "Custom Top Ribbon Text") are free-text fields supplied
 * by the product-options app, while the matching surcharge (for example "Custom Top Ribbon +$30")
 * is a native Shopify variant. Nothing ties the two together, so a customer could type ribbon text,
 * leave the ribbon set to "No", and check out without paying for it.
 *
 * This guard ties them together on the product page:
 *   - typing text into the guarded field selects the paid option (the price updates normally);
 *   - choosing the free option again clears the field, so text never rides along for free;
 *   - Add to Cart is blocked for one click if the two ever disagree (autofill, back button, etc.).
 *
 * The decision logic is plain functions (exported for the Node unit tests in pricing-tests/).
 * Only confirmed rules belong in RULES: add a rule only after the owner has confirmed the charge,
 * and mirror it in pricing-tests/manifest/pricing-manifest.json (the test suite checks they match).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.IronInkPricingGuard = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var RULES = [
    {
      handles: ['california-highway-patrol-star'],
      option: 'Top Ribbon',
      paidValue: 'Custom Top Ribbon +$30',
      textProperty: 'Custom Top Ribbon Text (if applicable)',
      note: 'A custom top ribbon is a +$30 option, so we selected it for you.',
      clearedNote: 'Top ribbon set to "No", so the ribbon text was cleared.'
    },
    {
      handles: ['chp-dispatch-star'],
      option: 'Honorably Retired Top Ribbon',
      paidValue: 'Honorably Retired +$30',
      textProperty: 'Custom Top Ribbon Text (if applicable)',
      note: 'A custom top ribbon is the +$30 Honorably Retired ribbon option, so we selected it for you.',
      clearedNote: 'Top ribbon set to "No", so the ribbon text was cleared.'
    },
    {
      handles: ['chp-wing-and-wheel'],
      option: 'Top Ribbon-Plaque',
      paidValue: 'Plaque (+$54.99)',
      textProperty: 'Plaque Text',
      note: 'Plaque text comes with the Plaque option (+$54.99), so we selected it for you. If you want a top ribbon instead, choose Volunteer or Explorer.',
      clearedNote: 'Plaque set to "None", so the plaque text was cleared.'
    },
    {
      handles: ['cdcr-star'],
      option: 'Top Ribbon',
      paidValue: 'Custom Top Ribbon +$30',
      textProperty: 'Custom Top Ribbon Text (if applicable)',
      note: 'A custom top ribbon is a +$30 option, so we selected it for you.',
      clearedNote: 'Top ribbon set to "None", so the ribbon text was cleared.'
    }
  ];

  // ---------- pure logic ----------
  function clean(v) { return String(v == null ? '' : v).replace(/\s+/g, ' ').trim(); }
  function hasText(v) { return clean(v).length > 0; }
  function isFreeChoice(v) { return /^(no|none|no thanks|no ribbon|n\/a)$/i.test(clean(v)); }

  function ruleForHandle(handle, rules) {
    var list = rules || RULES;
    for (var i = 0; i < list.length; i++) {
      if (list[i].handles.indexOf(handle) !== -1) return list[i];
    }
    return null;
  }

  /** What should happen right now, given the selected option value and the text typed? */
  function evaluate(rule, state) {
    if (hasText(state.text) && isFreeChoice(state.selected)) {
      return { ok: false, action: 'select-paid', paidValue: rule.paidValue, message: rule.note };
    }
    return { ok: true, action: 'none' };
  }

  /** What should happen when the customer picks an option value? */
  function onOptionChosen(rule, value, text) {
    if (isFreeChoice(value) && hasText(text)) {
      return { action: 'clear-text', message: rule.clearedNote };
    }
    return { action: 'none' };
  }

  var api = {
    RULES: RULES, evaluate: evaluate, onOptionChosen: onOptionChosen,
    ruleForHandle: ruleForHandle, isFreeChoice: isFreeChoice, hasText: hasText
  };

  // ---------- page wiring (browser only) ----------
  if (typeof document === 'undefined') return api;

  function findOptionRadios(rule) {
    // The option radios sit in their own fieldset and are tied to the cart form by a form="..." attribute.
    var fieldsets = document.querySelectorAll('fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend');
      if (legend && clean(legend.textContent) === rule.option) {
        return Array.prototype.slice.call(fieldsets[i].querySelectorAll('input[type="radio"]'));
      }
    }
    return [];
  }
  function selectedValue(radios) {
    for (var i = 0; i < radios.length; i++) if (radios[i].checked) return radios[i].value;
    return '';
  }
  function findTextField(rule) {
    var all = document.querySelectorAll('[name^="properties["]');
    var want = 'properties[' + rule.textProperty + ']';
    for (var i = 0; i < all.length; i++) if (all[i].name === want) return all[i];
    return null;
  }
  function showNote(field, message) {
    var host = (field.closest && field.closest('.form-group')) || field.parentNode;
    var note = host.querySelector('.iw-pricing-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'iw-pricing-note';
      note.setAttribute('role', 'status');
      note.style.cssText = 'margin:6px 0 0;font-size:1.3rem;line-height:1.4;';
      host.appendChild(note);
    }
    note.textContent = message;
  }

  function isOptionRadio(el, rule) {
    if (!el || el.type !== 'radio' || !el.closest) return false;
    var fs = el.closest('fieldset');
    var legend = fs && fs.querySelector('legend');
    return !!legend && clean(legend.textContent) === rule.option;
  }

  function init(options) {
    var rule = ruleForHandle(options && options.handle, options && options.rules);
    if (!rule) return false;

    // The theme re-draws the option buttons whenever the variant changes, so nothing is cached here:
    // the radios and the text box are looked up fresh every time, and the listeners sit on the document.
    function enforce() {
      var field = findTextField(rule);
      var radios = findOptionRadios(rule);
      if (!field || !radios.length) return true;
      var verdict = evaluate(rule, { selected: selectedValue(radios), text: field.value });
      if (verdict.ok) return true;
      for (var i = 0; i < radios.length; i++) {
        if (clean(radios[i].value) === clean(rule.paidValue) && !radios[i].checked) { radios[i].click(); break; }
      }
      showNote(field, verdict.message);
      return false;
    }

    document.addEventListener('input', function (e) {
      var field = findTextField(rule);
      if (field && e.target === field) enforce();
    });
    document.addEventListener('change', function (e) {
      var field = findTextField(rule);
      if (!field) return;
      if (e.target === field) { enforce(); return; }
      if (isOptionRadio(e.target, rule)) {
        var verdict = onOptionChosen(rule, e.target.value, field.value);
        if (verdict.action === 'clear-text') {
          field.value = '';
          field.dispatchEvent(new Event('input', { bubbles: true }));
          showNote(field, verdict.message);
        }
      }
    });

    // Last line of defence: if the two disagree when Add to Cart is pressed, fix it and stop for one click.
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('button[name="add"], input[name="add"]');
      if (btn && !enforce()) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.matches && f.matches('form[action*="/cart/add"]') && !enforce()) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);

    // The options app adds its text boxes after the page loads, and the Back button can restore typed text
    // without firing events, so check again when the box appears and when the page is shown.
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () { if (findTextField(rule)) { mo.disconnect(); enforce(); } });
      mo.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () { mo.disconnect(); }, 15000);
    }
    enforce();
    window.addEventListener('pageshow', enforce);
    return true;
  }

  api.init = init;
  return api;
});
